import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Leaf, Plane, Waves, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gImg = (id) => `${process.env.REACT_APP_GDRIVE_IMG_BASE}/${id}=s1200`;

const PROGRAMS_DATA = {
  agrilearn: {
    title: "AgriLearn Campus",
    tagline: "Earth-First Education: 7.5 Acres of Living Science",
    focus: "Agriculture · Ecology · Sustainability · Life Sciences",
    grades: "Nursery through Grade 12",
    icon: Leaf,
    heroImage: process.env.REACT_APP_AGRILEARN_IMG,
    bannerImage: gImg("1OexTzhTOekhl6xfzfTAHLIVNkQB0CfMQ"),
    description: "Authentic farm & agriculture immersion across 7.5 acres of living science. Hands-on planting, nurturing, harvesting mulberry crops, learning sericulture, soil science, plant physiology, and ecosystem balance. Meeting farm animals, understanding animal husbandry, exploring composting and natural pest management. Witnessing the full cycle from seed to silk.",
    focusAreas: ["Environmental Science", "Biology", "Sustainability", "Geography", "Life Skills"],
    experiences: [
      { title: "The Soil-to-Seed Journey", desc: "Ploughing, planting, irrigation, soil types, pH levels, and crop rotation" },
      { title: "Livestock Ethology", desc: "Supervised interaction with farm animals, animal husbandry, symbiotic relationship between fauna and flora" },
      { title: "Crop Science & Sustainability", desc: "Modern agricultural technology, traditional organic practices, and food systems" },
      { title: "Environmental Responsibility", desc: "Ecological interdependence, water conservation, and sustainable farming" },
    ],
    galleryImages: [
      gImg("1OexTzhTOekhl6xfzfTAHLIVNkQB0CfMQ"),
      gImg("1NNETfdN4waU6BIE3i6LkgrlK3Rh_M4ph"),
      gImg("1MoendjIeZK7l-I2Q9J0x2ZC7rKr8NtwJ"),
    ],
    pdfUrl: process.env.REACT_APP_AGRILEARN_PDF,
  },
  aerolab: {
    title: "AeroLab Experience",
    tagline: "The Physics of Flight: Real-World Aerodynamics",
    focus: "Aviation · Physics · Engineering · Aerospace Science",
    grades: "Grade 5 through Grade 12",
    icon: Plane,
    heroImage: process.env.REACT_APP_AEROLAB_IMG,
    bannerImage: gImg("1wGmByQchmEbucvVeVI76QkAnnT6QV6EZ"),
    description: "An exclusive, behind-the-scenes look at real aircraft in a private hangar at Jakkur Aerodrome. Features fully operational light aircraft. Students step inside aircraft, engage with instrumentation, and ask questions of certified aviation professionals.",
    focusAreas: ["Physics", "Engineering", "STEM Careers", "Aerospace Science"],
    experiences: [
      { title: "Structural Engineering", desc: "Examination of 2- to 5-seater aircraft assembly, material science, airframe integrity" },
      { title: "Aeronautical Principles", desc: "Workshops on lift, drag, thrust, and weight" },
      { title: "Cockpit Access", desc: "Interaction with real flight instruments, controls, and systems under expert supervision" },
      { title: "Industry Horizons", desc: "Introduction to aviation and aerospace careers" },
    ],
    galleryImages: [
      gImg("1wGmByQchmEbucvVeVI76QkAnnT6QV6EZ"),
      gImg("1fp9nAQ3MUBaOtu63eVVg6tLuMamtQjZ1"),
      gImg("1FB9rrC-tMMxehDYmp2rDD6Bp-j0lGqfP"),
    ],
    pdfUrl: process.env.REACT_APP_AEROLAB_PDF,
  },
  sailscience: {
    title: "SailScience Program",
    tagline: "Mastering the Elements: The Science of Sailing",
    focus: "Maritime Science · Physics · Leadership · Outdoor Education",
    grades: "Grade 5 through Grade 12",
    icon: Waves,
    heroImage: process.env.REACT_APP_SAILSCIENCE_IMG,
    bannerImage: gImg("1GCZOCOXCdmvJX_aA8-LDLQBboEspxX4D"),
    description: "An extraordinary day on the water at KRS Backwaters, in association with the Karnataka State Sailing Association. Students take the helm, read the wind, adjust sails, and feel the precise relationship between air, water, and motion. Physics becomes personal.",
    focusAreas: ["Physics", "Fluid Dynamics", "Navigation", "Teamwork", "Outdoor Education"],
    experiences: [
      { title: "Applied Fluid Dynamics", desc: "How wind velocity, water resistance, and buoyancy dictate vessel movement" },
      { title: "Navigation & Logic", desc: "Mathematics of maritime navigation, reading weather and current patterns" },
      { title: "Hands-on Sailing", desc: "Taking the helm under expert supervision-45 minutes of sailing" },
      { title: "Leadership & Coordination", desc: "Deck management developing communication, trust, and team decision-making" },
    ],
    galleryImages: [
      gImg("1GCZOCOXCdmvJX_aA8-LDLQBboEspxX4D"),
      gImg("17WldjQduRTsKWQOqSudjmKuKe8E95slU"),
      gImg("1of8qMXoB_YT0DCl2uC5SrouB6nNydsO1"),
    ],
    pdfUrl: process.env.REACT_APP_SAILSCIENCE_PDF,
  },
};

export default function ProgramPage() {
  const { slug } = useParams();
  const program = PROGRAMS_DATA[slug];
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", school_name: "", designation: "", mobile: "", email: "" });

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.mobile) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/brochure-download`, { ...form, program: program?.title });
      toast.success("Details sent! Downloading program PDF...");
      setShowForm(false);
      setForm({ name: "", school_name: "", designation: "", mobile: "", email: "" });
      window.open(program?.pdfUrl, '_blank');
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl mb-4">Program Not Found</h1>
          <Link to="/" className="text-primary hover:text-accent">Go Home</Link>
        </div>
      </div>
    );
  }

  const Icon = program.icon;

  return (
    <div className="min-h-screen" data-testid={`program-page-${slug}`}>
      <Header />

      {/* Hero with banner image */}
      <section className="relative h-[60vh] min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={program.bannerImage} alt={program.title} className="w-full h-full object-cover" loading="eager"
            onError={(e) => { e.target.src = program.heroImage; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/90 via-[#0D1B2A]/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-12 w-full">
          <Link to="/" onClick={() => { setTimeout(() => { document.querySelector("#programs")?.scrollIntoView({ behavior: "smooth" }); }, 100); }} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-5">
            <ArrowLeft className="w-4 h-4" /> Back to Programs
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <Icon className="w-6 h-6 text-accent" />
            <span className="text-sm tracking-[0.12em] uppercase text-white/60">{program.focus}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-white tracking-tighter mb-2">
            {program.title}
          </h1>
          <p className="text-lg text-accent font-medium">{program.tagline}</p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-14 md:py-18">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl font-medium text-foreground mb-5 tracking-tight">Program Overview</h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-8">{program.description}</p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mb-12">
            <button onClick={() => setShowForm(true)} data-testid="program-download-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-medium text-sm rounded-sm hover:bg-primary/90 transition-all hover:-translate-y-0.5">
              <Download className="w-4 h-4" /> Download Program Details
            </button>
            <Link to="/" onClick={() => { setTimeout(() => { document.querySelector("#inquiry")?.scrollIntoView({ behavior: "smooth" }); }, 100); }}
              data-testid="program-inquiry-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground font-medium text-sm rounded-sm hover:bg-accent/90 transition-all hover:-translate-y-0.5">
              Submit Enquiry <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Gallery images above The Experience */}
          <div className="grid grid-cols-3 gap-3 mb-10">
            {program.galleryImages.map((img, i) => (
              <div key={i} className="aspect-[4/3] overflow-hidden rounded-sm">
                <img src={img} alt={`${program.title} activity ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy"
                  onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
              </div>
            ))}
          </div>

          <h3 className="font-heading text-2xl font-medium text-foreground mb-5">The Experience</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {program.experiences.map((exp, i) => (
              <div key={i} className="p-5 border border-border/60 bg-card hover:shadow-md transition-shadow">
                <h4 className="font-heading text-lg font-medium text-foreground mb-1.5">{exp.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{exp.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />

      {showForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-sm p-8 max-w-md w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="program-download-modal">
            <h3 className="font-heading text-2xl font-medium text-foreground mb-2">Download {program.title} Details</h3>
            <p className="text-sm text-muted-foreground mb-6">Fill in your details to receive the program brochure.</p>
            <form onSubmit={handleDownload} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Full Name *</label>
                <Input placeholder="Your name" value={form.name} onChange={(e) => { const v = e.target.value.replace(/[^a-zA-Z\s]/g, ''); setForm(p => ({...p, name: v})); }} className="border-border/60" pattern="[A-Za-z\s]+" title="Letters only" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">School / Institution</label>
                <Input placeholder="School name" value={form.school_name} onChange={(e) => { const v = e.target.value.replace(/[^a-zA-Z0-9\s.'-]/g, ''); setForm(p => ({...p, school_name: v})); }} className="border-border/60" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Designation</label>
                <Input placeholder="e.g. Principal, Trustee, Coordinator" value={form.designation} onChange={(e) => setForm(p => ({...p, designation: e.target.value}))} className="border-border/60" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Mobile Number *</label>
                <Input type="tel" placeholder="+91 XXXXX XXXXX" value={form.mobile} onChange={(e) => { const v = e.target.value.replace(/[^0-9+\s-]/g, ''); setForm(p => ({...p, mobile: v})); }} className="border-border/60" pattern="[\d+\s-]{10,15}" title="Valid phone number" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Email Address *</label>
                <Input type="email" placeholder="email@school.com" value={form.email} onChange={(e) => setForm(p => ({...p, email: e.target.value}))} className="border-border/60" required />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={loading} className="flex-1 px-6 py-3 bg-accent text-accent-foreground font-medium text-sm rounded-sm hover:bg-accent/90 transition-all disabled:opacity-50">
                  {loading ? "Sending..." : "Download Now"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-3 border border-border/60 text-foreground font-medium text-sm rounded-sm hover:bg-muted transition-all">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
